import React, { useState, useEffect } from 'react';
import { UserPlus, Loader, Trash2, X, Shield, Edit2, AlertTriangle } from 'lucide-react';
import ApiService from './ApiService';
import UserRolesModal from './UserRolesModal';

const UserManagementPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [errorModal, setErrorModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    temporaryPassword: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [rolesUser, setRolesUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Check if user is admin
  const isAdmin = currentUser?.groups?.includes('Admins') || currentUser?.permissions?.includes('users.view');

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      temporaryPassword: ''
    });
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage('');
      const result = await ApiService.createUser(formData);

      if (result.temporaryPassword) {
        setMessage(`User created successfully! Temporary password: ${result.temporaryPassword}`);
      } else {
        setMessage('User created successfully!');
      }

      setFormData({ email: '', name: '', temporaryPassword: '' });

      // Wait 2 seconds to show the message, then close modal
      setTimeout(() => {
        setShowCreateModal(false);
        setMessage('');
        loadUsers(); // Reload user list
      }, 3000);
    } catch (err) {
      console.error('Error creating user:', err);
      setMessage('Failed to create user: ' + err.message);
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage('');

      await ApiService.updateUser(editingUser.username, {
        name: formData.name,
        newPassword: formData.temporaryPassword
      });

      setMessage('User updated successfully!');

      setTimeout(() => {
        setShowEditModal(false);
        setEditingUser(null);
        setMessage('');
        setFormData({ email: '', name: '', temporaryPassword: '' });
        loadUsers();
      }, 2000);
    } catch (err) {
      console.error('Error updating user:', err);
      setMessage('Failed to update user: ' + err.message);
      setSaving(false);
    }
  };

  const handleDeleteUser = async (username) => {
    try {
      setSaving(true);
      await ApiService.deleteUser(username);
      setDeleteConfirm(null);
      loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setErrorModal({ title: 'Error Deleting User', message: 'Failed to delete user: ' + (err.message || 'Unknown error') });
    } finally {
      setSaving(false);
    }
  };

  const handleManageRoles = (user) => {
    setRolesUser(user);
    setShowRolesModal(true);
  };

  const handleRolesModalClose = () => {
    setShowRolesModal(false);
    setRolesUser(null);
  };

  const handleRolesModalSave = () => {
    setShowRolesModal(false);
    setRolesUser(null);
    loadUsers();
  };

  // Access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 text-center">
          <Shield className="mx-auto mb-4 text-red-600" size={64} />
          <h1 className="text-2xl font-bold text-red-900 mb-2">Access Denied</h1>
          <p className="text-red-700">
            You do not have permission to access user management. Only administrators can manage users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">User Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700 flex items-center gap-2"
        >
          <UserPlus size={20} />
          Create User
        </button>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="text-center py-12">
            <Loader className="animate-spin mx-auto mb-2" size={32} />
            <p className="text-gray-500">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-4">No users found</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700"
            >
              Create your first user
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Name</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Username</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Role</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Created</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Last Login</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.username} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{user.name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                    <td className="px-6 py-4">
                      {(() => {
                        const rbacRoles = user.rbacRoles || [];

                        if (rbacRoles.length === 0) {
                          return <span className="text-gray-400 text-xs">No roles assigned</span>;
                        }

                        return (
                          <>
                            {rbacRoles.map((role) => (
                              <span
                                key={role.roleId}
                                className="inline-block px-2 py-1 text-xs font-medium bg-spruce-100 text-spruce-800 rounded-full mr-1 mb-1"
                              >
                                {role.roleName}
                              </span>
                            ))}
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${user.enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}
                      >
                        {user.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.lastLogin ? (
                        <>
                          {new Date(user.lastLogin).toLocaleDateString()}
                          <div className="text-xs text-gray-400">
                            {new Date(user.lastLogin).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </>
                      ) : (
                        'Never'
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleManageRoles(user)}
                          className="p-2 text-spruce-600 hover:bg-spruce-50 rounded transition"
                          title="Manage roles"
                        >
                          <Shield size={18} />
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 text-spruce-600 hover:bg-spruce-50 rounded transition"
                          title="Edit user"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(user)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                          title="Delete user"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <UserPlus size={24} />
                Create New User
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setMessage('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., pleasehelp@gmail.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">This will be used to log in</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Temporary Password (optional)
                </label>
                <input
                  type="text"
                  value={formData.temporaryPassword}
                  onChange={(e) => setFormData({ ...formData, temporaryPassword: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Leave blank for auto-generated"
                />
                <p className="text-xs text-gray-500 mt-1">
                  User will be required to change password on first login
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  Password must be at least 8 characters and include: uppercase, lowercase, number, and special character
                </p>
              </div>

              {message && (
                <div className={`p-3 rounded-lg ${message.includes('successfully') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {message}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus size={18} />
                      Create User
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setMessage('');
                  }}
                  disabled={saving}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Edit2 size={24} />
                Edit User
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                  setMessage('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.email}
                  className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100"
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password (optional)
                </label>
                <input
                  type="text"
                  value={formData.temporaryPassword}
                  onChange={(e) => setFormData({ ...formData, temporaryPassword: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Leave blank to keep current password"
                />
                <p className="text-xs text-orange-600 mt-1">
                  Password must be at least 8 characters and include: uppercase, lowercase, number, and special character
                </p>
              </div>

              {message && (
                <div className={`p-3 rounded-lg ${message.includes('successfully') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  {message}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Edit2 size={18} />
                      Update User
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                    setMessage('');
                  }}
                  disabled={saving}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Delete User?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.name || deleteConfirm.email}</strong>?
            </p>
            <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDeleteUser(deleteConfirm.username)}
                disabled={saving}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Yes, Delete
                  </>
                )}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Roles Modal */}
      {showRolesModal && rolesUser && (
        <UserRolesModal
          user={rolesUser}
          onClose={handleRolesModalClose}
          onSave={handleRolesModalSave}
        />
      )}

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;